import { CreateStatusPageDTO, StatusPageDTO } from '@meshwatch/types';
import { CreateTableOutput, DeleteItemOutput } from 'aws-sdk/clients/dynamodb';
import { AWSError } from 'aws-sdk/lib/error';
import { PromiseResult } from 'aws-sdk/lib/request';
import uuid from 'uuid';

import {
  DEFAULT_DYNAMO_DB_CLIENT,
  DynamoDBClient,
  DynamoDBTableClient,
} from '../../shared/aws/dynamodb';
import { NotFoundException } from '../../shared/rest';
import DateUtils from '../../util/date';

import {
  STATUS_PAGE_TABLE_CUSTOM_DOMAIN_GSI,
  STATUS_PAGE_TABLE_SCHEMA,
  STATUS_PAGE_TABLE_SEARCH_GSI,
} from './schema';
import { StatusPageDynamoItem } from './types';

export interface StatusPageDatasource {
  getStatusPage(id: string): Promise<StatusPageDTO>;
  lookupStatusPage(customDomain: string): Promise<StatusPageDTO>;
  createStatusPage(params: CreateStatusPageDTO): Promise<StatusPageDTO>;
  findStatusPages(userId: string): Promise<StatusPageDTO[]>;
  deleteStatusPage(id: string): Promise<PromiseResult<DeleteItemOutput, AWSError>>;
  updateStatusPage(params: StatusPageDTO): Promise<StatusPageDTO>;
}

export class DynamoStatusPageDatasource implements StatusPageDatasource {
  private readonly dynamoClient: DynamoDBTableClient;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    this.dynamoClient = new DynamoDBTableClient(STATUS_PAGE_TABLE_SCHEMA.TableName, dynamoClient);
  }

  public createTable = (): Promise<PromiseResult<CreateTableOutput, AWSError>> => {
    return this.dynamoClient.createTable(STATUS_PAGE_TABLE_SCHEMA);
  };

  public updateStatusPage = (params: StatusPageDTO) => {
    return this.putStatusPage(params);
  };

  public createStatusPage = (params: CreateStatusPageDTO) => {
    const statusPage: StatusPageDTO = {
      id: uuid.v4(),
      created: new Date(),
      ...params,
    };
    return this.putStatusPage(statusPage);
  };

  private putStatusPage = (statusPage: StatusPageDTO) => {
    const Item = this.statusPageToDynamoItem(statusPage);
    return this.dynamoClient.putDocumentToDynamo({ Item }).then(_ => statusPage);
  };

  public findStatusPages = (userId: string) => {
    const userIdHashKey = this.encodeUserId(userId);
    return this.dynamoClient
      .queryTable({
        IndexName: STATUS_PAGE_TABLE_SEARCH_GSI,
        KeyConditionExpression: `userId = :userId`,
        ExpressionAttributeValues: {
          ':userId': userIdHashKey,
        },
      })
      .then(resp => {
        const dynamoItems = resp.Items as StatusPageDynamoItem[];
        return dynamoItems.map(this.statusPageFromDynamoItem);
      });
  };

  public deleteStatusPage = (id: string) => {
    return this.dynamoClient.deleteItem({ Key: { id: this.encodeId(id) } });
  };

  public lookupStatusPage = (customDomain: string) => {
    return this.dynamoClient
      .queryTable({
        IndexName: STATUS_PAGE_TABLE_CUSTOM_DOMAIN_GSI,
        KeyConditionExpression: `customDomain = :customDomain`,
        ExpressionAttributeValues: {
          ':customDomain': customDomain,
        },
      })
      .then(resp => {
        if (!resp.Items || resp.Items.length === 0) {
          throw new NotFoundException(`Could not find statusPage: customDomain = ${customDomain}`);
        }
        const dynamoItem = resp.Items[0] as StatusPageDynamoItem;
        return this.statusPageFromDynamoItem(dynamoItem);
      });
  };

  public getStatusPage = (id: string) => {
    return this.dynamoClient
      .queryTable({
        KeyConditionExpression: `id = :id`,
        ExpressionAttributeValues: {
          ':id': this.encodeId(id),
        },
      })
      .then(resp => {
        if (!resp.Items || resp.Items.length === 0) {
          throw new NotFoundException(`Could not find statusPage: id = ${id}`);
        }
        const dynamoItem = resp.Items[0] as StatusPageDynamoItem;
        return this.statusPageFromDynamoItem(dynamoItem);
      });
  };

  private statusPageToDynamoItem = (statusPage: StatusPageDTO): StatusPageDynamoItem => {
    const userId = this.encodeUserId(statusPage.userId);
    const id = this.encodeId(statusPage.id);

    return {
      userId,
      id,
      monitors: statusPage.monitors,
      created: DateUtils.getUnixTimestamp(statusPage.created),
      includeAllMonitors: statusPage.includeAllMonitors,
      niceName: statusPage.niceName,
      logo: statusPage.logo,
      customDomain: statusPage.customDomain,
      customCss: statusPage.customCss,
      customHtmlFooter: statusPage.customHtmlFooter,
      customHtmlHeader: statusPage.customHtmlHeader,
    };
  };

  private statusPageFromDynamoItem = (Item: StatusPageDynamoItem): StatusPageDTO => {
    const userId = this.decodeHashKey(Item.userId);
    const id = this.decodeHashKey(Item.id);

    return {
      id,
      userId,
      monitors: Item.monitors,
      created: DateUtils.dateFromUnixTimestamp(Item.created),
      includeAllMonitors: Item.includeAllMonitors,
      niceName: Item.niceName,
      logo: Item.logo,
      customDomain: Item.customDomain,
      customCss: Item.customCss,
      customHtmlFooter: Item.customHtmlFooter,
      customHtmlHeader: Item.customHtmlHeader,
    };
  };

  private encodeId(statusPageId: string): string {
    return `id#${statusPageId}`;
  }

  private encodeUserId(userId: string): string {
    return `userId#${userId}`;
  }

  private decodeHashKey(hashKey: string): string {
    const s = hashKey.split('#');
    return s[1];
  }
}

const dynamoStatusPageDatasource = new DynamoStatusPageDatasource();

export default dynamoStatusPageDatasource;
